import {
  ArrowRightOutlined,
  CopyOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { Button, Space, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import cn from 'classnames';
import type { FormTemplate } from '../../models/TaskForm/TaskForm';
import type { TaskFormTemplatesRequest } from '../../store/reducers/TaskFormSlice';
import sharedCellStyles from '../shared/tableCellContent.module.css';
import styles from './questionnaireTemplatesList.module.css';

export interface TemplateTableRow {
  readonly key: string;
  readonly id: string;
  readonly name: string;
  readonly questions: number;
  readonly createdAt: string;
  readonly user: string;
}

export interface TemplateTableActions {
  readonly canManage: boolean;
  readonly deleting: boolean;
  readonly copyingIds: ReadonlySet<string>;
  readonly onCopy: (template: FormTemplate) => void;
  readonly onDelete: (templateId: string, templateName: string) => void;
  readonly onOpen: (template: FormTemplate) => void;
}

export const SORT_KEY_BY_COLUMN = {
  name: 'name',
  questions: 'questions.Count()',
  createdAt: 'createdDateTime',
} as const;

const toAntSortOrder = (sortOrder?: string) => {
  if (sortOrder === 'Asc') return 'ascend' as const;
  if (sortOrder === 'Desc') return 'descend' as const;
  return null;
};

interface ColumnOptions {
  readonly request: TaskFormTemplatesRequest;
  readonly templates: readonly FormTemplate[];
  readonly loading: boolean;
  readonly actions: TemplateTableActions;
}

export const getQuestionnaireTemplateColumns = ({
  request,
  templates,
  loading,
  actions,
}: ColumnOptions): ColumnsType<TemplateTableRow> => {
  return [
    {
      title: <div className={sharedCellStyles.headerCenter}>Название</div>,
      dataIndex: 'name',
      sorter: true,
      sortOrder:
        request.SortKeySelector === SORT_KEY_BY_COLUMN.name
          ? toAntSortOrder(request.SortOrder)
          : null,
      render: (text: string) => (
        <div className={cn(styles.tableCell, styles.tableCellLeft)}>
          <span className={sharedCellStyles.textPrimary}>{text}</span>
        </div>
      ),
    },
    {
      title: <div className={sharedCellStyles.headerCenter}>Вопросы</div>,
      dataIndex: 'questions',
      sorter: true,
      sortOrder:
        request.SortKeySelector === SORT_KEY_BY_COLUMN.questions
          ? toAntSortOrder(request.SortOrder)
          : null,
      width: 120,
      render: (value: number) => (
        <div className={cn(styles.tableCell, sharedCellStyles.cellCenter)}>
          <span className={sharedCellStyles.textPrimary}>{value}</span>
        </div>
      ),
    },
    {
      title: <div className={sharedCellStyles.headerCenter}>Дата создания</div>,
      dataIndex: 'createdAt',
      width: 180,
      sorter: true,
      sortOrder:
        request.SortKeySelector === SORT_KEY_BY_COLUMN.createdAt
          ? toAntSortOrder(request.SortOrder)
          : null,
      render: (value: string) => (
        <div className={cn(styles.tableCell, sharedCellStyles.cellCenter)}>
          <span className={sharedCellStyles.textSecondary}>{value}</span>
        </div>
      ),
    },
    {
      title: <div className={sharedCellStyles.headerCenter}>Пользователь</div>,
      dataIndex: 'user',
      width: 180,
      render: (value: string) => (
        <div className={cn(styles.tableCell, sharedCellStyles.cellCenter)}>
          <span className={sharedCellStyles.textSecondary}>{value || '—'}</span>
        </div>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      align: 'right',
      render: (_, record) => {
        const entity = templates.find((t) => t.id === record.id);
        const copying = actions.copyingIds.has(record.id);

        return (
          <Space size={12} className={styles.actionsCell}>
            {actions.canManage && (
              <Tooltip title="Удалить анкету">
                <Button
                  data-testid="template-action-delete"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label="Удалить анкету"
                  title="Удалить анкету"
                  disabled={actions.deleting || loading}
                  onClick={() => actions.onDelete(record.id, record.name)}
                />
              </Tooltip>
            )}

            {actions.canManage && (
              <Tooltip title="Копировать анкету">
                <Button
                  data-testid="template-action-copy"
                  type="text"
                  icon={<CopyOutlined />}
                  aria-label="Копировать анкету"
                  title="Копировать анкету"
                  disabled={copying || loading}
                  onClick={() => entity && !copying && actions.onCopy(entity)}
                />
              </Tooltip>
            )}

            <Tooltip title="Открыть детали">
              <Button
                data-testid="template-action-open"
                type="text"
                icon={<ArrowRightOutlined />}
                aria-label="Открыть детали"
                title="Открыть детали"
                disabled={loading}
                onClick={() => entity && !loading && actions.onOpen(entity)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];
};
